import { Cable, House, List as Menu, Package, ShieldCheck, ShoppingCart, SignOut, UserCircle as UserRound, X } from '../ui/icons';
import { lazy, Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import { useI18n } from '../../hooks/useI18n';
import Logo from '../ui/Logo';
import BrandMark from '../ui/BrandMark';
import { useAppStore } from '../../store/appStore';

const AdminCommandPalette = lazy(() => import('../admin/AdminCommandPalette'));

export default function Header() {
  const open = useAppStore((state) => state.mobileMenuOpen);
  const setMobileMenuOpen = useAppStore((state) => state.setMobileMenuOpen);
  const location = useLocation();
  const { getItemCount } = useCart();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [adminHeaderOpen, setAdminHeaderOpen] = useState(false);
  const cartCount = getItemCount();
  const accountPath = user?.is_admin ? '/admin' : '/profile';
  const accountLabel = user?.is_admin ? t('admin_panel_title') : t('nav_profile');
  const accountActive = location.pathname === accountPath;
  const isAdminRoute = Boolean(user?.is_admin && location.pathname.startsWith('/admin'));
  const nav = [
    { path: '/', label: 'nav_home', Icon: House },
    { path: '/products', label: 'nav_products', Icon: Package },
    { path: '/services', label: 'nav_services', Icon: Cable },
  ];

  // Scroll progress indicator
  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      document.documentElement.style.setProperty('--scroll-pct', `${scrollPercent}%`);
    };

    window.addEventListener('scroll', updateScrollProgress, { passive: true });
    updateScrollProgress(); // Initial call

    return () => {
      window.removeEventListener('scroll', updateScrollProgress);
    };
  }, []);


  const links = (mobile = false) => (
    <>
      {nav.map(({ path, label, Icon }) => (
        <Link
          key={path}
          to={path}
          onClick={() => setMobileMenuOpen(false)}
          title={!mobile ? t(label) : undefined}
          className={mobile
            ? `flex items-center gap-3 rounded-lg border border-glass-line/15 bg-glass/5 px-3 py-3 text-left font-medium ${(path === '/products' ? location.pathname.startsWith(path) : location.pathname === path) ? 'border-glass-line/50 bg-glass/10 text-brand' : 'text-muted hover:border-brand/45 hover:text-brand'}`
            : `flex h-9 w-9 items-center justify-center rounded-lg border border-glass-line/15 bg-glass/5 transition-colors ${(path === '/products' ? location.pathname.startsWith(path) : location.pathname === path) ? 'border-glass-line/50 text-brand' : 'text-muted hover:border-brand/45 hover:text-brand'}`}
        >
          <Icon className={mobile ? 'h-5 w-5' : 'h-4 w-4'} />
          {mobile ? t(label) : <span className="sr-only">{t(label)}</span>}
        </Link>
      ))}
      {user?.is_admin && mobile && (
        <Link to="/admin?section=catalog" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg border border-glass-line/15 bg-glass/5 px-3 py-3 font-medium ${location.pathname.startsWith('/admin') ? 'border-glass-line/50 bg-glass/10 text-brand' : 'text-muted hover:border-brand/45 hover:text-brand'}`}>
          <ShieldCheck className="h-5 w-5" />
          {t('admin_panel_title')}
        </Link>
      )}
      {user ? (
        mobile ? (
          <Link to={accountPath} onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg border border-glass-line/15 bg-glass/5 px-3 py-3 font-medium ${accountActive ? 'border-glass-line/50 bg-glass/10 text-brand' : 'text-muted hover:border-brand/45 hover:text-brand'}`}>
            {user.avatar_url ? <img src={user.avatar_url} alt="" referrerPolicy="no-referrer" className="h-9 w-9 shrink-0 rounded-full border border-glass-line/30 object-cover" /> : <UserRound className="h-9 w-9 shrink-0 rounded-full border border-glass-line/30 p-2" />}
            <span className="min-w-0"><span className="block truncate">{user.name || accountLabel}</span><span className="block text-xs font-normal text-subtle">{accountLabel}</span></span>
          </Link>
        ) : (
          <Link to={accountPath} title={user.name || accountLabel} className={`flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border transition-colors ${accountActive ? 'border-brand/50' : 'border-glass-line/15 hover:border-brand/45'}`} aria-label={accountLabel}>
            {user.avatar_url ? <img src={user.avatar_url} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" /> : <UserRound className={`h-5 w-5 ${accountActive ? 'text-brand' : 'text-muted'}`} />}
          </Link>
        )
      ) : (
          <Link to="/login" onClick={() => setMobileMenuOpen(false)} title={!mobile ? t('nav_login') : undefined} className={mobile ? `flex items-center gap-3 rounded-lg border border-glass-line/15 bg-glass/5 px-3 py-3 font-medium ${location.pathname === '/login' ? 'border-glass-line/50 bg-glass/10 text-brand' : 'text-muted hover:border-brand/45 hover:text-brand'}` : `flex h-9 w-9 items-center justify-center rounded-lg border border-glass-line/15 bg-glass/5 transition-colors ${location.pathname === '/login' ? 'border-glass-line/50 text-brand' : 'text-muted hover:border-brand/45 hover:text-brand'}`}>
          <UserRound className={mobile ? 'h-5 w-5' : 'h-5 w-5'} />
          {mobile ? t('nav_login') : <span className="sr-only">{t('nav_login')}</span>}
        </Link>
      )}
      {user && mobile && (
        <button type="button" onClick={() => { logout(); setMobileMenuOpen(false); }} className="flex items-center gap-3 rounded-lg border border-danger/30 bg-danger/10 px-3 py-3 font-medium text-danger transition-colors hover:border-danger/50 hover:bg-danger/20">
          <SignOut className="h-5 w-5" />
          {t('common_logout')}
        </button>
      )}
    </>
  );


  if (isAdminRoute) {
    return (
      <header className="fixed inset-x-0 top-0 z-30" aria-label={t('admin_navigation_label')}>
        <Suspense fallback={null}><AdminCommandPalette showTrigger={false} /></Suspense>
        <button type="button" onClick={() => setAdminHeaderOpen((value) => !value)} aria-expanded={adminHeaderOpen} aria-label={t('admin_navigation_label')} className="fixed right-3 top-3 flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-surface/95 text-brand shadow-lg shadow-canvas/40 backdrop-blur-xl transition-transform hover:scale-105">
          <Menu className="h-7 w-7" />
        </button>
        {adminHeaderOpen && createPortal(
          <div className="pointer-events-auto fixed inset-0 z-50 bg-glass-overlay/45 p-3 backdrop-blur-sm" onClick={() => setAdminHeaderOpen(false)}>
            <nav className="ml-auto w-full max-w-sm rounded-2xl border border-line bg-surface p-3 shadow-2xl shadow-canvas/50" onClick={(event) => { event.stopPropagation(); setAdminHeaderOpen(false); }}>
              <div className="mb-3 flex items-center justify-between border-b border-line pb-3"><Link to="/" aria-label="TECNICELU S.A. home"><Logo variant="wordmark" wordmarkClassName="h-7 w-auto object-contain" /></Link><BrandMark className="h-8 w-8" /></div>
              <div className="flex flex-col gap-2 text-lg">{links(true)}</div>
            </nav>
          </div>,
          document.body,
        )}
      </header>
    );
  }

  return (
    <header className="header-glass py-3">
      <nav className="relative z-[60] mx-auto flex max-w-7xl items-center justify-between px-6 sm:px-6 lg:px-10 xl:px-14 2xl:px-20">
        <span className="header-scan" aria-hidden="true" />
        <div className="flex shrink-0 items-center gap-3 sm:gap-4">
          <div className="relative shrink-0">
            <Link to="/" aria-label="TECNICELU S.A. home">
              <BrandMark className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16" />
            </Link>
            <a
              href="https://www.youtube.com/@losrecomendados-co"
              target="_blank"
              rel="noreferrer"
              aria-label={t('header_losrecomendados')}
              title={t('header_losrecomendados')}
              className="absolute -bottom-1 -left-1 flex h-4 w-4 items-center justify-center rounded-full border border-glass-line/60 bg-surface p-px shadow-md shadow-brand/20 transition-transform hover:scale-110 sm:-left-4 sm:h-5 sm:w-5"
            >
              <img src="https://assets.tecnicelu.store/logos/losrecomendados-logo.png" alt="" className="block h-full w-full rounded-full object-cover" />
            </a>
          </div>
        </div>

        <Link to="/" aria-label="TECNICELU S.A. home" className="header-wordmark-wrap animated absolute left-1/2 -translate-x-1/2">
          <Logo variant="wordmark" wordmarkClassName="header-logo-word h-auto w-[calc(100vw-14rem)] max-h-10 object-contain sm:w-auto sm:max-h-12 sm:max-w-[calc(100vw-22rem)] md:max-h-14" />
        </Link>

        <div className="ml-3 flex shrink-0 items-center gap-2 sm:ml-4 sm:gap-3">
          <div className="hidden items-center gap-2 xl:flex">{links()}</div>
          <Link to="/cart" data-testid="cart-icon" className="relative glass flex h-9 w-9 items-center justify-center rounded-lg border-success/35 text-success transition-all hover:border-success/60 hover:bg-success/15" aria-label={t('nav_cart')}>
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-success-strong text-[10px] font-bold text-success-contrast">{cartCount}</span>}
          </Link>
          <button className="flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-glass/5 hover:text-brand xl:hidden" onClick={() => setMobileMenuOpen(!open)} aria-label={open ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={open}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && createPortal(
        <div className="fixed inset-0 z-[55] bg-glass-overlay/80 backdrop-blur-sm xl:hidden" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-panel absolute inset-x-0 top-16 max-h-[calc(100dvh-4rem)] overflow-y-auto p-6 sm:top-[4.5rem] sm:max-h-[calc(100dvh-4.5rem)] md:top-[5.5rem] md:max-h-[calc(100dvh-5.5rem)]" onClick={(event) => event.stopPropagation()}>
            <div className="relative rounded-2xl border border-glass-line/15 bg-surface p-3">
              <button type="button" onClick={() => setMobileMenuOpen(false)} aria-label="Cerrar menú" className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-glass/5 hover:text-brand">
                <X className="h-6 w-6" />
              </button>
              <nav className="flex flex-col gap-2 text-lg pt-10">
                {links(true)}
              </nav>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}
